import { ASSESSMENT_PROPOSALS_ENABLED } from "../constants/featureFlags";

// 403 は HTTP status だけでは正常な拒否か判別できない。API と共有する理由コードだけを除外する。
// ASSESSMENT_PROPOSALS_DISABLED は査定提案の提供停止中（Trello #147）の書き込み拒否。agent 側で停止している間だけ
// 想定内とし、再開（フラグ true）後に API 側だけ停止のまま食い違った場合は未知の 403 として報告されるようにする。
const EXPECTED_FORBIDDEN_CODES = [
  "AGENT_ACCESS_BLOCKED",
  "AGENT_IMPERSONATION_READ_ONLY",
  ...(ASSESSMENT_PROPOSALS_ENABLED ? [] : ["ASSESSMENT_PROPOSALS_DISABLED"]),
];
const reportedApiErrors = new WeakSet<object>();

type ApiErrorLike = {
  status?: unknown;
  statusCode?: unknown;
  body?: { code?: unknown };
  response?: ApiErrorLike | null;
};

const asApiError = (error: unknown): ApiErrorLike | undefined =>
  error && typeof error === "object" ? (error as ApiErrorLike) : undefined;

export const getApiErrorStatus = (error: unknown): unknown => {
  const value = asApiError(error);
  return value?.status ?? value?.statusCode ?? value?.response?.status ?? value?.response?.statusCode;
};

export const getApiErrorCode = (error: unknown): unknown => {
  const value = asApiError(error);
  return value?.body?.code ?? value?.response?.body?.code;
};

export const isAccountAccessBlocked = (error: unknown): boolean =>
  getApiErrorStatus(error) === 403 && getApiErrorCode(error) === "AGENT_ACCESS_BLOCKED";

export const shouldSkipAuthOrCacheErrorReport = (status: unknown, code?: unknown): boolean =>
  status === 304 ||
  status === 401 ||
  (status === 403 && typeof code === "string" && EXPECTED_FORBIDDEN_CODES.includes(code));

// 同じレスポンスが interceptor と console / unhandledrejection の両方に流れても、一度だけ報告する。
// 弱参照なのでレスポンスや個人情報をセッション全体で保持しない。
export const markApiErrorReported = (error: unknown): void => {
  if (error && typeof error === "object") reportedApiErrors.add(error);
};

export const wasApiErrorReported = (error: unknown): boolean => {
  const value = asApiError(error);
  return !!value && (reportedApiErrors.has(value) || (!!value.response && reportedApiErrors.has(value.response)));
};

export const shouldSkipConsoleErrorReport = (error: unknown): boolean =>
  wasApiErrorReported(error) || shouldSkipAuthOrCacheErrorReport(getApiErrorStatus(error), getApiErrorCode(error));

// CaptureConsole の生の引数は hint.captureContext.extra.arguments に残る。
// event.extra は SDK の正規化後なので、そこから status やオブジェクトの同一性を推測しない。
export const shouldSkipApiErrorEvent = (hint: { originalException?: unknown; captureContext?: unknown }): boolean => {
  if (shouldSkipConsoleErrorReport(hint.originalException)) return true;
  if (hint.originalException instanceof Error) return false;
  const context = hint.captureContext as { extra?: { arguments?: unknown[] } } | undefined;
  const args = context?.extra?.arguments;
  return Array.isArray(args) && args.some(shouldSkipConsoleErrorReport);
};
